/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, useCallback, useEffect, useState, type CSSProperties } from 'react';

import { cssMap, keyframes, jsx } from '@compiled/react';
import { createPortal } from 'react-dom';

import { token, useThemeObserver } from '@atlaskit/tokens';

const LIGHT_COLORS = ['#FCA700', '#6A9A23', '#1868DB', '#AF59E0'];
const DARK_COLORS = ['#FCA700', '#82B536', '#357DE8', '#BF63F3'];

const svgStrokeAnimation = keyframes({
	from: {
		strokeDashoffset: 'var(--stroke-perimeter)',
	},
	to: {
		strokeDashoffset: 0,
	},
});

const styles = cssMap({
	overlay: {
		position: 'absolute',
		insetBlockEnd: 0,
		insetBlockStart: 0,
		insetInlineEnd: 0,
		insetInlineStart: 0,
		pointerEvents: 'none',
		animation: `${svgStrokeAnimation} 1.5s linear infinite`,
		strokeOpacity: 1,
	},
	svg: {
		position: 'absolute',
		fill: 'transparent',
	},
});

type AnimatedGradientBorderProps = {
	borderRadius?: boolean;
	containerElement: HTMLElement | null;
	isGenerating?: boolean;
};

type StrokePerimeterStyle = CSSProperties & {
	'--stroke-perimeter': number;
};

export const AnimatedGradientBorder = ({
	borderRadius = true,
	containerElement,
	isGenerating = false,
}: AnimatedGradientBorderProps): JSX.Element | null => {
	const { colorMode } = useThemeObserver();
	const colors = colorMode === 'dark' ? DARK_COLORS : LIGHT_COLORS;
	const [perimeter, setPerimeter] = useState(0);

	useEffect(() => {
		if (!containerElement || !isGenerating) {
			return;
		}

		const rafId = requestAnimationFrame(() => {
			const rect = containerElement.getBoundingClientRect();
			setPerimeter(Math.round((rect.height + rect.width) * 2));
		});

		return () => cancelAnimationFrame(rafId);
	}, [containerElement, isGenerating]);

	const calculateDashArray = useCallback(
		(renderPosition: number) => {
			if (renderPosition === 0) {
				return 0;
			}
			const dashArrayLength = Math.round(
				(perimeter / colors.length) * (colors.length - renderPosition),
			);
			const dashOffset = perimeter - dashArrayLength;
			return `${dashArrayLength} ${dashOffset}`;
		},
		[colors, perimeter],
	);

	if (!containerElement || !isGenerating) {
		return null;
	}

	const overlayStyle: StrokePerimeterStyle = { '--stroke-perimeter': perimeter };

	return createPortal(
		<div
			css={styles.overlay}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
			style={overlayStyle}
		>
			{colors.map((color, index) => (
				<Fragment key={color}>
					<svg
						css={styles.svg}
						height="100%"
						width="100%"
						xmlns="http://www.w3.org/2000/svg"
					>
						<rect
							data-testid={`animated-${color}`}
							height="98%"
							rx={borderRadius ? '4' : '0'}
							ry={borderRadius ? '4' : '0'}
							stroke={color}
							strokeDasharray={calculateDashArray(index)}
							strokeWidth={token('space.025')}
							width="99.8%"
							x="1"
							y="1"
						/>
					</svg>
				</Fragment>
			))}
		</div>,
		containerElement,
	);
};
